import { Link, useLocation } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import {
  AppWindow,
  HelpCircle,
  History,
  Inbox,
  LogOut,
  Map,
  Menu,
  MessageSquare,
  Moon,
  NotebookPen,
  Puzzle,
  Settings,
  Sun,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";

import { UnreadBadge } from "@/components/inbox/UnreadBadge";
import { CreditBalancePill } from "@/components/layout/CreditBalancePill";
import { BrandLogo } from "@/components/ui/BrandLogo";
import { SupportModal } from "@/components/support/SupportModal";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";
import { useChatPanelStore } from "@/store/chatPanelStore";
import { useEngineStore } from "@/store/useEngineStore";
import { api } from "@convex/_generated/api";

interface RecentThread {
  _id: string;
  title: string;
}

interface TopBarProps {
  onSignOut: () => void;
  onNewChat?: () => void;
  recentThreads?: RecentThread[];
}

export function TopBar({ onSignOut, onNewChat, recentThreads }: TopBarProps) {
  const [supportOpen, setSupportOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const credit = useCreditBalance();
  const unreadCount = useQuery(api.emails.countUnread) ?? 0;
  const { pathname } = useLocation();
  // Thread lock (frontend-doctrine §4.16) — the mobile twin of the rail's
  // suppression. Hidden, not disabled; never engages on `"loading"`.
  const newChatHidden = useOnboardingStatus().mode === "onboarding";
  const { boardHidden, setBoardHidden } = useChatPanelStore();
  const isChatRoute = pathname.startsWith("/chat/");
  // A focused prop window (Mailbox, Files…) is a bottom sheet that owns the
  // bottom of the screen — hide the bar underneath it while it's open.
  const propWindowOpen = useEngineStore((s) => s.activePropIds.length > 0);

  // Always close the mobile drawer after a navigation. "New Chat" navigates
  // programmatically (which can race the inline setState), so relying on the
  // click handler alone left the drawer open over the new chat.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  return (
    <>
      {/* Mobile bottom action bar — the primary controls live in the thumb
          zone instead of the top corners. Menu on the left; on a chat thread,
          an animated Chat/World segmented toggle. Hidden on lg+, where the
          desktop sidebar and two-panel layout own navigation. */}
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-3 border-t border-black/10 bg-white/90 px-4 pt-2 backdrop-blur-md lg:hidden dark:border-white/10 dark:bg-surface-dark/90",
          propWindowOpen && "hidden",
        )}
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
      >
        <button
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
          className="flex h-11 w-11 items-center justify-center rounded-full text-black/70 transition-transform active:scale-90 dark:text-white/70"
        >
          <Menu className="h-6 w-6" />
        </button>

        {isChatRoute && (
          <div className="relative flex items-center rounded-full bg-black/5 p-1 dark:bg-white/10">
            {[
              { label: "Chat", icon: MessageSquare, hidden: true },
              { label: "World", icon: Map, hidden: false },
            ].map((seg) => {
              const active = boardHidden === seg.hidden;
              const Icon = seg.icon;
              return (
                <button
                  key={seg.label}
                  onClick={() => setBoardHidden(seg.hidden)}
                  aria-pressed={active}
                  className="relative flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold"
                >
                  {active && (
                    <motion.span
                      layoutId="mobileViewSeg"
                      className="absolute inset-0 rounded-full bg-white shadow-sm dark:bg-white/20"
                      transition={{ type: "spring", stiffness: 500, damping: 34 }}
                    />
                  )}
                  <span
                    className={cn(
                      "relative z-10 flex items-center gap-1.5",
                      active ? "text-black dark:text-white" : "text-black/50 dark:text-white/50",
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={2.4} />
                    {seg.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, x: "-100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "-100%" }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-0 z-50 flex w-full flex-col bg-surface-light pt-[env(safe-area-inset-top)] dark:bg-surface-dark lg:hidden"
            >
              {/* Header */}
              <div className="flex h-20 items-center justify-between px-6">
                <BrandLogo markClassName="h-8" />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-black/50 hover:bg-black/5 dark:text-white/50 dark:hover:bg-white/5"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Nav items */}
              <nav className="flex-1 space-y-1 overflow-y-auto px-4">
                {!newChatHidden && (
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onNewChat?.();
                    }}
                    className="mb-4 flex w-full items-center gap-3 rounded-full bg-home-yellow px-5 py-3 text-sm font-bold text-home-ink shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)]"
                  >
                    <MessageSquare className="h-5 w-5" />
                    New Chat
                  </button>
                )}
                <Link
                  to="/inbox"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-black/60 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  <Inbox className="h-5 w-5" />
                  Inbox
                  <UnreadBadge count={unreadCount} />
                </Link>
                {/* Tasks + Workflows hidden from the nav (operator decision,
                    2026-08-08) — the routes stay live and URL-reachable. */}
                <Link
                  to="/studio"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-black/60 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  <AppWindow className="h-5 w-5" />
                  Studio
                </Link>
                <Link
                  to="/notebook"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-black/60 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  <NotebookPen className="h-5 w-5" />
                  Notebook
                </Link>
                <Link
                  to="/integrations"
                  search={{ connect: undefined }}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-black/60 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  <Puzzle className="h-5 w-5" />
                  Integrations
                </Link>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-black/60 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  <Settings className="h-5 w-5" />
                  Settings
                </Link>

                {/* Recent conversations */}
                {recentThreads && recentThreads.length > 0 && (
                  <>
                    <div className="my-4 border-t border-black/10 dark:border-white/10" />
                    <div className="px-4 pb-2 text-[10px] font-black uppercase tracking-widest text-black/40 dark:text-white/40">
                      History
                    </div>
                    {recentThreads.map((thread) => (
                      <Link
                        key={thread._id}
                        to="/chat/$threadId"
                        params={{ threadId: thread._id }}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-medium text-black/50 transition-colors hover:bg-black/5 hover:text-black dark:text-white/50 dark:hover:bg-white/5 dark:hover:text-white"
                      >
                        <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{thread.title}</span>
                      </Link>
                    ))}
                    <Link
                      to="/chat-history"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold text-brand-coral transition-colors hover:bg-brand-coral/5"
                    >
                      <History className="h-3.5 w-3.5 shrink-0" />
                      All Chats
                    </Link>
                  </>
                )}
              </nav>

              {/* Bottom actions */}
              <div className="space-y-1 border-t border-black/10 px-4 py-4 dark:border-white/10">
                <div className="mb-3 flex justify-start px-4">
                  <CreditBalancePill balance={credit.balance} status={credit.status} />
                </div>
                <button
                  onClick={() => {
                    toggleTheme();
                    setMobileMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-black/60 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  {isDark ? (
                    <Sun className="h-5 w-5" />
                  ) : (
                    <Moon className="h-5 w-5" />
                  )}
                  {isDark ? "Light Mode" : "Dark Mode"}
                </button>
                <button
                  onClick={() => {
                    setSupportOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-black/60 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  <HelpCircle className="h-5 w-5" />
                  Support
                </button>
                <button
                  onClick={() => {
                    onSignOut();
                    setMobileMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-black/60 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white"
                >
                  <LogOut className="h-5 w-5" />
                  Sign Out
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <SupportModal
        isOpen={supportOpen}
        onClose={() => setSupportOpen(false)}
      />
    </>
  );
}
